import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

import { api } from '../services/client';
import { useTranslation } from '../i18n';
import LanguageSwitcher from './LanguageSwitcher';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

interface ILoginProps {
  onLogin: (token: string, user: any) => void;
}

const Login = ({ onLogin }: ILoginProps) => {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { admin, token } = await api.post<{ admin: any; token: string }>(
        '/backoffice/auth/login',
        { username, password },
      );
      onLogin(token, admin);
    } catch (err: any) {
      setError(err.message || t('login.network_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-5">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-xl font-bold text-foreground">
            {t('login.title')}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('login.subtitle')}
          </p>
        </div>

        <Card className="shadow-sm p-8">
          <CardContent className="p-0">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {t('login.username')}
                </label>
                <Input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={t('login.username_placeholder')}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {t('login.password')}
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('login.password_placeholder')}
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 text-red-600 px-4 py-2.5 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <Button type="submit" disabled={loading} className="w-full">
                {loading ? t('login.loading') : t('login.submit')}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="flex items-center justify-end mt-4">
          <LanguageSwitcher />
        </div>
      </div>
    </div>
  );
};

export default Login;
